/**
 * 클라이언트 도구 — 어시스턴트 서비스가 SSE `client_tool` 로 넘긴 도구를 **이 브라우저의 화면에서** 실행한다.
 *
 * 두 부류가 한 실행 경로를 쓴다:
 *   ① LLM 에 선언된 도구 (tenants/openflow.json 의 client:true) — get_usage_guide · open_sample_workflow · focus_node · point_at · suggest_actions.
 *      모두 읽기이거나 화면 이동이다. 캔버스를 바꾸는 도구는 LLM 에 주지 않는다 — 어시스턴트는 제안(propose_workflow)만 하고
 *      반영은 사용자가 누른다는 OpenFlow 의 원칙 때문이다.
 *   ② 도움말 Quick Start 의 「대신 해줘」 — create_workflow · add_node · connect_nodes · save_workflow · deploy_workflow ·
 *      run_workflow · count_table_rows. LLM 이 아니라 **미리 정해 둔 호출**을 사용자가 버튼으로 실행하는 것이라 캔버스를
 *      바꾸고 배포·실행까지 해도 된다(사용자가 헤더의 Save·Deploy·실행을 누르는 것과 같은 API). helpWorkflow.QUICK_START_STEPS 참조.
 *   ②를 따로 만들지 않고 여기 두는 이유: 실행 함수가 두 벌이면 한쪽만 고쳐진다(datamodeler 이식 체크리스트 3번).
 *
 * 노드는 툴박스 드롭(WorkflowCanvas.onDrop)과 같은 모양으로 만든다 — id·기본 config·className 까지.
 */
import { isCanvasDirty } from '../../../utils/canvasSnapshot';
import type { Node, Edge } from '@xyflow/react';
import store from '../../../store';
import { usageTopics, findTopic } from '../../../help/usageGuide';
import { openWorkflow } from '../../../store/workflow/openWorkflow';
import { createWorkflow, saveWorkflowCanvas, Workflow } from '../../../store/project/projectSlice';
import { addNode, addEdge, setSelectedNode, centerOnNode, replaceCanvas, setRunStatus, setRunDurations, clearRunStatus } from '../../../store/workflow/workflowSlice';
import { be1Fetch } from '../../../config/api';
import { dagIdOf, triggerRun, pollRun, stateLabel } from '../../../services/executionApi';
import { computeNodeRunStatus, computeNodeDurations } from '../utils/runStatus';
import { getId, getDefaultNodeData } from '../canvas/WorkflowCanvas';
import { findFreePosition, nodeSize } from '../utils/nodeLayout';
import { markAssistantSelection } from '../../../help/helpBus';
import { pointAt, isPointTarget, POINT_TARGET_HELP, waitPan } from '../../../help/pointer';
import { typeLabelOf } from '../nodeCatalog';
import { assertWritable, WriteAction } from '../../../services/canvasSchema';
import i18n from '../../../i18n';
import { errorText } from '../../../services/errorText';
import { withTimeZone } from '../../../utils/timeZone';

const tr = (key: string, opts?: Record<string, unknown>) => String(i18n.t(`assistant:tools.${key}`, opts as any));

export interface ClientToolCall { name: string; input: Record<string, any> }
export interface ClientToolResult { ok: boolean; [k: string]: unknown }

/** 패널이 받는 부수 효과 — suggest_actions 는 화면(마지막 메시지의 버튼)에만 뜻이 있다 */
export interface ClientToolHooks {
  onSuggest?: (options: string[]) => void;
  /** 오래 걸리는 도구(run_workflow)의 진행 글 — 도구 줄에 표시 */
  onProgress?: (text: string) => void;
}

const nodesNow = (): Node[] => (store.getState() as any).workflow.nodes as Node[];
const currentWorkflow = () => (store.getState() as any).workflow.current as { id: string; name: string } | null;

/** 라벨(또는 id)로 노드 찾기 — 같은 라벨이 여럿이면 마지막에 넣은 것.
    customId 는 예전 캔버스에만 있는 값이라 참조만 받아 준다(새 노드에는 붙지 않는다) */
function findNode(ref: string): Node | undefined {
  const q = String(ref || '').trim();
  const all = nodesNow();
  return [...all].reverse().find((n) => n.id === q || (n.data as any)?.customId === q || String((n.data as any)?.label || '').trim() === q)
    || [...all].reverse().find((n) => String((n.data as any)?.label || '').includes(q));
}

/**
 * 「대신 해줘」가 버튼을 누르는 것처럼 보이게 — 손가락이 그 버튼으로 가서 한 번 누른 뒤 도구가 돈다.
 * 사람이 화면에서 같은 일을 하려면 어디를 누르는지 배우는 자리다 (help/pointer).
 */
const pressFirst = (target: string, name: string) =>
  pointAt(target, { tap: true, hold: 1800, label: String(i18n.t('assistant:pointer.pressing', { name })) });

/** 선택 + 중앙 패닝 — 사용자는 채팅을 보고 있어 어디가 바뀌었는지 모른다. AI 탭은 그대로 둔다(markAssistantSelection) */
function focus(nodeId: string) {
  markAssistantSelection();
  store.dispatch(setSelectedNode(nodeId));
  store.dispatch(centerOnNode(nodeId));
}

function summarizeNode(n: Node) {
  return { id: n.id, type: n.type, label: (n.data as any)?.label, customId: (n.data as any)?.customId };
}

// ------------------------------------------------------------------ 도구들

function getUsageGuide(input: { topic?: string }): ClientToolResult {
  if (!input?.topic) {
    return { ok: true, topics: usageTopics().map((t) => ({ id: t.id, title: t.title, summary: t.summary, sampleWorkflow: t.sampleWorkflow || null })) };
  }
  const t = findTopic(input.topic);
  if (!t) return { ok: false, error: tr('noTopic', { topic: input.topic }), topics: usageTopics().map((x) => x.id) };
  return { ok: true, topic: { id: t.id, title: t.title, summary: t.summary, steps: t.steps, sampleWorkflow: t.sampleWorkflow || null, point: t.point || null } };
}

async function openSampleWorkflow(input: { name?: string }): Promise<ClientToolResult> {
  const st = store.getState() as any;
  const projectId = st.project.selectedProjectId as string | null;
  const all = st.project.workflows as Workflow[];
  const mine = projectId ? all.filter((w) => w.projectId === projectId) : all;
  const q = String(input?.name || '샘플①').trim();   // i18n: 샘플 워크플로 이름(데이터)이라 번역하지 않는다
  const wf = mine.find((w) => w.name.startsWith(q)) || mine.find((w) => w.name.includes(q));
  if (!wf) {
    return { ok: false, error: tr('noSample', { name: q }), available: mine.map((w) => w.name).slice(0, 30) };
  }
  await openWorkflow(store, wf);
  return { ok: true, workflow: { id: wf.id, name: wf.name }, nodes: nodesNow().map(summarizeNode) };
}

async function createWorkflowTool(input: { name: string; description?: string; folder?: string; reset?: boolean }): Promise<ClientToolResult> {
  const st = store.getState() as any;
  const projectId = st.project.selectedProjectId as string | null;
  if (!projectId) return { ok: false, error: tr('selectProject') };
  const name = String(input?.name || '').trim();
  if (!name) return { ok: false, error: tr('emptyName') };
  // 같은 이름이 이미 있으면 새로 만들지 않고 그것을 연다 — Quick Start 를 다시 돌려도 연습용이 쌓이지 않게
  const existing = (st.project.workflows as Workflow[]).find((w) => w.projectId === projectId && w.name === name);
  if (existing) {
    await openWorkflow(store, existing);
    // reset: Quick Start 는 빈 캔버스에서 시작해야 한다 — 지난 연습이 남아 있으면 비운다 (언두 한 단계, Ctrl+Z 로 돌아온다)
    if (input.reset && nodesNow().length) store.dispatch(replaceCanvas({ nodes: [], edges: [] }));
    return { ok: true, workflow: { id: existing.id, name: existing.name }, reused: true, cleared: !!input.reset };
  }
  const r = await store.dispatch(createWorkflow({ projectId, name, description: input.description, icon: '📄', folder: input.folder }) as any);
  if (createWorkflow.rejected.match(r)) return { ok: false, error: (r as any).error?.message || tr('createFailed') };
  const created = r.payload as Workflow;
  const inStore = (store.getState() as any).project.workflows.find((w: Workflow) => w.id === created.id) || { ...created, nodes: [], edges: [] };
  await openWorkflow(store, inStore);
  return { ok: true, workflow: { id: created.id, name: created.name } };
}

async function addNodeTool(input: { type: string; label?: string; config?: Record<string, unknown>; position?: { x: number; y: number } }): Promise<ClientToolResult> {
  if (!currentWorkflow()) return { ok: false, error: tr('noOpenWorkflowHint') };
  const type = String(input?.type || '').trim();
  const base = getDefaultNodeData(type);
  if (!type || !base) return { ok: false, error: tr('unknownNodeType', { type }) };
  // 툴박스의 그 항목을 먼저 누른다 — 어디서 가져오는지 보이게
  await pointAt(`toolbox:${type}`, { tap: true, hold: 4000, label: String(i18n.t('assistant:pointer.adding', { name: typeLabelOf(type) })) });
  const existing = nodesNow().filter((n) => !n.parentId);
  // 맨 오른쪽 노드의 오른쪽 옆 — 없으면 캔버스 시작 자리. 겹치면 가장 가까운 빈 자리로
  const rightmost = existing.reduce<Node | null>((a, n) => (!a || n.position.x > a.position.x ? n : a), null);
  // position 을 주면 그 자리(분기처럼 위·아래로 갈라 놓을 때), 아니면 맨 오른쪽 옆
  const desired = input.position && Number.isFinite(input.position.x) && Number.isFinite(input.position.y)
    ? { x: input.position.x, y: input.position.y }
    : rightmost ? { x: rightmost.position.x + nodeSize(rightmost).width + 110, y: rightmost.position.y } : { x: 40, y: 120 };
  const position = findFreePosition(existing, desired, nodeSize({ type } as any));
  const label = input.label || base.label;
  const node: Node = {
    id: getId(),
    type,
    position,
    data: { ...base, label, config: { ...(base as any).config, ...(input.config || {}), label } },
    draggable: true,
    className: 'workflow-child-node',
    style: { zIndex: 10 },
  };
  store.dispatch(addNode(node));
  focus(node.id);
  // 캔버스가 새 노드로 옮겨 가는 동안 손가락은 툴박스에서 기다렸다가, 멈춘 뒤 천천히 날아간다
  await waitPan();
  await pointAt(`node:${node.id}`, { hold: 2500, label, flyMs: 1100 });
  return { ok: true, node: summarizeNode(node) };
}

/** sourceHandle: 분기의 FALSE 는 'false-path', 품질검사의 위반은 'reject-path' (기본 핸들은 생략) */
function connectNodesTool(input: { source: string; target: string; sourceHandle?: string }): ClientToolResult {
  const s = findNode(input?.source);
  const t = findNode(input?.target);
  if (!s || !t) return { ok: false, error: tr('nodeNotFound', { ref: !s ? input?.source : input?.target }), nodes: nodesNow().map(summarizeNode) };
  if (s.id === t.id) return { ok: false, error: tr('sameNode') };
  const id = `e${s.id}-${t.id}`;
  const edges = (store.getState() as any).workflow.edges as Edge[];
  if (edges.some((e) => e.source === s.id && e.target === t.id)) return { ok: true, edge: { id, source: s.id, target: t.id }, existed: true };
  const handle = input.sourceHandle ? { sourceHandle: input.sourceHandle } : {};
  store.dispatch(addEdge({ id, source: s.id, target: t.id, type: 'workflow', ...handle } as Edge));
  focus(t.id);
  return { ok: true, edge: { id, source: s.id, target: t.id } };
}

function focusNodeTool(input: { node: string }): ClientToolResult {
  const n = findNode(input?.node);
  if (!n) return { ok: false, error: tr('nodeNotFound', { ref: input?.node }), nodes: nodesNow().map(summarizeNode) };
  focus(n.id);
  waitPan().then(() => pointAt(`node:${n.id}`, { hold: 6000, label: String((n.data as any)?.label || ''), flyMs: 1000 }));
  return { ok: true, node: summarizeNode(n) };
}

/**
 * 화면의 한 곳을 손가락으로 가리킨다 — "툴박스 품질검사를 끌어 놓으세요" 같은 안내를 글로만 하지 않게.
 * 대상 키는 help/pointer.ts (toolbox:<유형> · toolbar:<동작> · header:<버튼> · panel:<영역> · node:<라벨>).
 */
async function pointAtTool(input: { target: string; label?: string }): Promise<ClientToolResult> {
  let target = String(input?.target || '').trim();
  if (!isPointTarget(target)) return { ok: false, error: tr('badPointTarget', { targets: POINT_TARGET_HELP }) };
  if (target.startsWith('node:')) {
    const n = findNode(target.slice(5));
    if (!n) return { ok: false, error: tr('nodeNotFound', { ref: target.slice(5) }), nodes: nodesNow().map(summarizeNode) };
    focus(n.id);
    target = `node:${n.id}`;
    await waitPan();
  }
  const found = await pointAt(target, { hold: 8000, flyMs: 900, label: input?.label ? String(input.label).slice(0, 120) : undefined });
  return found ? { ok: true, target } : { ok: false, error: tr('pointNotFound', { target }) };
}

// ------------------------------------------------------------------ Quick Start 전용: 저장·배포·실행·확인
// 헤더의 Save·Deploy·실행 버튼(ModernLayout)과 같은 API 를 부른다. 헤더는 배포 상태를 컴포넌트 안에 두므로 여기서 다시
// 배포해도 무방하다 — 헤더 실행은 캔버스가 바뀌었으면 어차피 재배포한다.

function canvasBody() {
  const st = store.getState() as any;
  const w = st.workflow;
  return { workflow_id: w.current?.id, name: w.current?.name, nodes: w.nodes, edges: w.edges, schedule_interval: w.settings?.schedule, settings: withTimeZone(w.settings) };
}

/**
 * 형식이 더 새로우면 도구를 실행하지 않는다. 저장은 thunk 가 막지만 배포·실행은 API 를 직접 부르므로
 * 여기서 한 번 더 본다 (canvasSchema.assertWritable 과 같은 판정).
 */
function schemaBlock(what: WriteAction): ClientToolResult | null {
  const info = (store.getState() as any).workflow.schemaInfo;
  try { assertWritable(info, what); return null; }
  catch (e) { return { ok: false, error: (e as Error).message }; }
}

/** 마지막 배포의 노드→태스크 매핑 — 실행 중 캔버스 노드에 상태 뱃지를 붙일 때 쓴다 */
let lastTaskMap: Record<string, string> = {};

async function saveWorkflowTool(): Promise<ClientToolResult> {
  await pressFirst('toolbar:save', String(i18n.t('common:ui.save')));
  const st = store.getState() as any;
  const cur = currentWorkflow();
  if (!cur) return { ok: false, error: tr('noOpenWorkflow') };
  // 마지막 저장과 같으면 새 버전을 만들지 않는다 (저장 버튼과 같은 규칙)
  const wf = (st.project.workflows || []).find((w: any) => w.id === cur.id);
  if (!isCanvasDirty(st.project.savedSnapshots?.[cur.id], st.workflow.nodes, st.workflow.edges, st.workflow.settings)) {
    return { ok: true, workflow: cur, verNo: wf?.curVerNo, summary: tr('noChanges', { name: cur.name, ver: wf?.curVerNo ?? 0 }) };
  }
  const r = await store.dispatch(saveWorkflowCanvas({ id: cur.id, nodes: st.workflow.nodes, edges: st.workflow.edges, settings: st.workflow.settings, regUsrid: st.auth?.user?.usrid || '' }) as any);
  if (saveWorkflowCanvas.rejected.match(r)) return { ok: false, error: (r as any).error?.message || tr('saveFailed') };
  const ver = (r.payload as any)?.verNo;
  return { ok: true, workflow: cur, verNo: ver, summary: tr('saved', { name: cur.name, ver }) };
}

async function deployWorkflowTool(): Promise<ClientToolResult> {
  await pressFirst('toolbar:deploy', String(i18n.t('common:ui.deploy')));
  const cur = currentWorkflow();
  if (!cur) return { ok: false, error: tr('noOpenWorkflow') };
  const blocked = schemaBlock('deploy');
  if (blocked) return blocked;
  const res = await be1Fetch('/api/canvas/deploy', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(canvasBody()) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: errorText(body, res.statusText) };
  lastTaskMap = body.task_map || {};
  store.dispatch(clearRunStatus());
  return { ok: true, dagId: body.dag_id, file: body.file, taskMap: lastTaskMap, summary: tr('deployed', { dagId: body.dag_id, tasks: Object.values(lastTaskMap).join(', ') || tr('none') }) };
}

/** 트리거하고 끝날 때까지(최대 3분) 기다린다 — 노드 뱃지는 헤더 실행과 같게 갱신 */
async function runWorkflowTool(input: { conf?: Record<string, string>; timeoutMs?: number }, hooks: ClientToolHooks): Promise<ClientToolResult> {
  await pressFirst('toolbar:run', String(i18n.t('common:ui.run')));
  const cur = currentWorkflow();
  if (!cur) return { ok: false, error: tr('noOpenWorkflow') };
  const blocked = schemaBlock('run');
  if (blocked) return blocked;
  const st = store.getState() as any;
  const nodes = st.workflow.nodes as Node[]; const edges = st.workflow.edges as Edge[];
  let runId: string;
  try { runId = await triggerRun(cur.id, input?.conf || {}); }
  catch (e) { return { ok: false, error: tr('triggerFailed', { message: (e as Error).message }) }; }
  store.dispatch(setRunStatus(computeNodeRunStatus(nodes, edges, lastTaskMap, [], null)));
  let lastTasks: any[] = [];
  const final = await pollRun(cur.id, runId, (state, tasks) => {
    lastTasks = tasks;
    store.dispatch(setRunStatus(computeNodeRunStatus(nodes, edges, lastTaskMap, tasks, state)));
    store.dispatch(setRunDurations(computeNodeDurations(lastTaskMap, tasks)));
    hooks.onProgress?.(tr('running', { state: stateLabel(state), tasks: tasks.map((tk) => `${tk.task_id}:${stateLabel(tk.state)}`).join(', ') }));
  }, { intervalMs: 3000, timeoutMs: input?.timeoutMs || 180000 });
  // 헤더 실행과 같게 — 최종 상태·소요시간을 서버 기준으로 다시 맞춘다 (RunStatusSync)
  window.dispatchEvent(new CustomEvent('openflow:run-finished'));
  const tasks = lastTasks.map((t) => `${t.task_id}:${stateLabel(t.state)}`).join(', ');
  if (final === 'success') return { ok: true, runId, state: final, tasks: lastTasks, summary: tr('runSuccess', { tasks }) };
  if (final === 'failed') return { ok: false, runId, state: final, tasks: lastTasks, error: tr('runFailed', { tasks }) };
  return { ok: false, runId, state: final, error: tr('runTimeout') };
}

/** 타깃 테이블 행 수 — 미리보기 API(읽기 전용 SELECT)로 센다 */
async function countTableRowsTool(input: { connection: string; table: string; schema?: string }): Promise<ClientToolResult> {
  const table = input?.schema ? `${input.schema}.${input.table}` : input?.table;
  if (!input?.connection || !table) return { ok: false, error: tr('needTable') };
  const res = await be1Fetch('/api/data/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind: 'db', connection: input.connection, query: `select count(*) as cnt from ${table}`, limit: 1 }) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: errorText(body, res.statusText) };
  const cnt = Number((body.rows || [])[0]?.cnt ?? (body.rows || [])[0]?.CNT ?? NaN);
  return { ok: true, table, count: cnt, summary: Number.isFinite(cnt) ? tr('tableRows', { table, n: cnt }) : tr('tableRowsUnknown', { table, body: JSON.stringify(body).slice(0, 200) }) };
}

/** SAM/CSV 파일 행 수 — 미리보기 API. 못 읽어도 실패로 끊지 않고 summary 로 알린다(파일은 실행 뒤에야 생기고 루트 밖일 수 있다) */
async function countFileRowsTool(input: { filepath: string; delimiter?: string; hasHeader?: boolean; encoding?: string }): Promise<ClientToolResult> {
  if (!input?.filepath) return { ok: false, error: tr('needFilepath') };
  const res = await be1Fetch('/api/data/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind: 'csv', filepath: input.filepath, delimiter: input.delimiter || ',', hasHeader: input.hasHeader !== false, encoding: input.encoding || 'UTF-8', limit: 500 }) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: true, readable: false, summary: tr('fileUnreadable', { path: input.filepath, detail: errorText(body, res.statusText) }) };
  const rows = body.rows || [];
  const sample = rows[0] ? Object.entries(rows[0]).map(([k, v]) => `${k}=${v}`).join(', ') : '';
  return { ok: true, readable: true, count: rows.length, columns: body.columns, summary: `${tr('fileRows', { path: input.filepath, n: rows.length })}${sample ? tr('firstRow', { sample }) : ''}` };
}

/** LLM 에 선언된 클라이언트 도구 — 이 목록 밖의 도구는 LLM 이 불러도 거부한다 (Quick Start 전용 도구를 LLM 이 쓰지 못하게) */
/**
 * 최근 오류 기록 — "왜 안 되죠?" 에 답하려면 실제로 무엇이 실패했는지 봐야 한다.
 *
 * 읽기 전용이고, 저장할 때 이미 비밀번호·SQL 원문·데이터 값이 걸러진 것만 남아 있다
 * (backend/app/services/event_log_store.py `_scrub`). 스택은 길어서 앞부분만 준다 — 어느 파일에서
 * 났는지 알기에는 충분하고, 대화에 넣기에는 전체가 너무 크다.
 */
async function recentErrorsTool(input: { hours?: number; limit?: number; feature?: string }): Promise<ClientToolResult> {
  const hours = Math.min(Math.max(Number(input.hours) || 24, 1), 24 * 7);
  const limit = Math.min(Math.max(Number(input.limit) || 20, 1), 50);
  const qs = new URLSearchParams({ kind: 'error', limit: String(limit), since: new Date(Date.now() - hours * 3600_000).toISOString() });
  if (input.feature) qs.set('feature', String(input.feature));
  const res = await be1Fetch(`/api/logs?${qs}`, { credentials: 'include' });
  if (!res.ok) return { ok: false, error: res.status === 401 ? tr('loginRequired') : tr('logFailed', { status: res.status }) };
  const body = await res.json();
  const items = (body.items || []).map((e: any) => ({
    ts: e.ts, feature: e.feature, target: e.target, user: e.user,
    message: e.error?.message, status: e.error?.status, where: e.error?.where,
    stack: typeof e.error?.stack === 'string' ? e.error.stack.slice(0, 600) : undefined,
  }));
  return { ok: true, hours, count: items.length, errors: items };
}

export const LLM_CLIENT_TOOLS = new Set(['get_usage_guide', 'open_sample_workflow', 'focus_node', 'point_at', 'suggest_actions', 'get_recent_errors']);

/**
 * 도구 하나 실행. 실패해도 던지지 않고 { ok:false, error } 로 돌려준다 — 서비스에 그대로 전달돼 LLM 이 다음 수를 정한다.
 * @param fromLlm true 면 LLM_CLIENT_TOOLS 만 허용
 */
export async function runClientTool(call: ClientToolCall, hooks: ClientToolHooks = {}, fromLlm = false): Promise<ClientToolResult> {
  const input = call.input || {};
  try {
    if (fromLlm && !LLM_CLIENT_TOOLS.has(call.name)) return { ok: false, error: tr('notAllowed', { name: call.name }) };
    switch (call.name) {
      case 'get_usage_guide': return getUsageGuide(input);
      case 'get_recent_errors': return await recentErrorsTool(input as any);
      case 'open_sample_workflow': return await openSampleWorkflow(input);
      case 'focus_node': return focusNodeTool(input as any);
      case 'point_at': return await pointAtTool(input as any);
      case 'suggest_actions': {
        const options = Array.isArray(input.options) ? input.options.map(String).filter(Boolean).slice(0, 4) : [];
        if (options.length < 1) return { ok: false, error: tr('emptyOptions') };
        hooks.onSuggest?.(options);
        return { ok: true, shown: options };
      }
      case 'create_workflow': return await createWorkflowTool(input as any);
      case 'add_node': return await addNodeTool(input as any);
      case 'connect_nodes': return connectNodesTool(input as any);
      case 'save_workflow': return await saveWorkflowTool();
      case 'deploy_workflow': return await deployWorkflowTool();
      case 'run_workflow': return await runWorkflowTool(input as any, hooks);
      case 'count_table_rows': return await countTableRowsTool(input as any);
      case 'count_file_rows': return await countFileRowsTool(input as any);
      default: return { ok: false, error: tr('unknownTool', { name: call.name }) };
    }
  } catch (err) {
    return { ok: false, error: (err as Error)?.message || String(err) };
  }
}
